import { Button, ButtonGroup, DatePicker, DateUtil, Divider, Input, MessageHelper } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import styled from 'styled-components';
import { ErrorMessageLabelNoIcon } from '../../../components/audience/SharedComponents';
import Constants from '../../../constants';
import ContentConstants from '../../../contentConstants';
import { AudiencePropertiesUnderValidation, IAudience, IEmailTemplate, IValidationErrorCollection } from '../../../models';
import { defaultAudience } from '../../../store/AudienceDetailsStore';
import { ValidationErrorHelper } from '../../../utilities/ValidationErrorHelper';
import * as AudienceValidation from '../../audience/AudienceValidation';
import { Timezone } from '../../audience/Timezone';
import { CardLayout, Col3Col9, LabelAbove, WarningModal } from '../../common';
import { RightButtonContainer } from '../../common/styles/globalStyles';

const DateContainer = styled.div`
	min-height: 390px;
`;
const OpenDatePicker = styled(DatePicker)`
	margin-right: 30px;
`;
const InputContainer = styled.div.attrs<{}, {}>({
	className: 'tws-date-container',
})`
	display: flex;
	margin-bottom: 15px;
	.tws-open-date-readonly {
		float: left;
		width: 218px;
		margin-right: 10px;
	}
	.tws-close-date-readonly {
		width: 218px;
		margin-right: 10px;
	}

	@media (min-width: 0) and (max-width: 1500px) {
		flex-direction: column;
	}
`;

export interface IAdminOpenCloseProps {
	audiences: IAudience[];
	loading: boolean;
	reminderEmails?: IEmailTemplate[];
	validationErrors?: IValidationErrorCollection;
	surveyId: number | undefined;
	audienceId: number;
	onChange: (audienceId: number, openImmediately: boolean, openOn?: string, closeOn?: string) => void;
	update: (audience: IAudience, isReopeningSurvey: boolean) => void;
}

export class AdminOpenClose extends React.Component<
	IAdminOpenCloseProps,
	{
		reminderEmails?: IEmailTemplate[];
		warningModalOpen: boolean;
		audience: IAudience;
	}
> {
	constructor(props: IAdminOpenCloseProps) {
		super(props);

		const audience = _.find(props.audiences, (a) => a.audienceId === props.audienceId) ?? defaultAudience;
		this.state = {
			reminderEmails: this.props.reminderEmails,
			warningModalOpen: false,
			audience,
		};

		this.getValidationError = this.getValidationError.bind(this);
		this.onOpenOnChange = this.onOpenOnChange.bind(this);
		this.onCloseOnChange = this.onCloseOnChange.bind(this);
		this.onOpenOnClear = this.onOpenOnClear.bind(this);
		this.onCloseOnClear = this.onCloseOnClear.bind(this);
		this.handleSave = this.handleSave.bind(this);
		this.closeModal = this.closeModal.bind(this);
	}

	public UNSAFE_componentWillReceiveProps(nextProps: IAdminOpenCloseProps) {
		const audience = _.find(nextProps.audiences, (a) => a.audienceId === nextProps.audienceId) ?? defaultAudience;
		if (
			this.state.audience.openOn !== audience.openOn ||
			this.state.audience.closeOn !== audience.closeOn ||
			this.state.audience.openImmediately !== audience.openImmediately
		) {
			this.validate(audience.openOn, audience.closeOn);
			this.setState({ audience });
		}
	}

	public validate(openOn: string | undefined, closeOn: string | undefined) {
		this.setState({ warningModalOpen: this.reminderDatesValid(openOn, closeOn) });
	}

	public render() {
		const { warningModalOpen } = this.state;
		return (
			<CardLayout loading={this.props.loading} title={this.getTitleForCardLayout()}>
				<DateContainer>{this.getDateContent()}</DateContainer>
				{this.getSaveContent()}
				{warningModalOpen && (
					<WarningModal
						isOpen={warningModalOpen}
						secondaryButtonText={ContentConstants.BUTTONS_CLOSE}
						onClose={this.closeModal}
						contentHeadline={ContentConstants.WARNING_MODAL_OPEN_CLOSE_DATES_HEADLINE}
						contentBody={ContentConstants.WARNING_MODAL_OPEN_CLOSE_DATES_BODY}
					/>
				)}
			</CardLayout>
		);
	}

	public getValidationError() {
		const { validationErrors } = this.props;
		const { audience } = this.state;

		const openCloseDateValidationError = AudienceValidation.validateAudienceOpenCloseDates(
			audience.openOn,
			audience.closeOn,
			audience.openImmediately,
			audience.isOpen,
		);
		let combinedValidationError = validationErrors;
		if (openCloseDateValidationError) {
			if (combinedValidationError) {
				combinedValidationError[AudiencePropertiesUnderValidation.OpenCloseDates] =
					openCloseDateValidationError[AudiencePropertiesUnderValidation.OpenCloseDates];
			} else {
				combinedValidationError = openCloseDateValidationError;
			}
		}
		return combinedValidationError;
	}

	public reminderDatesValid(openOn?: string, closeOn?: string) {
		const { reminderEmails } = this.props;

		let invalid = false;
		if (reminderEmails && reminderEmails.length > 0) {
			reminderEmails.forEach((reminderEmail) => {
				if (!invalid && reminderEmail.scheduledFor) {
					const closeOnMoment = moment(closeOn);
					const openOnMoment = moment(openOn);

					const scheduledFor = moment(reminderEmail.scheduledFor);
					if (scheduledFor.valueOf() < openOnMoment.valueOf() || scheduledFor.valueOf() > closeOnMoment.valueOf()) {
						invalid = true;
					}
				}
			});
		}
		return invalid;
	}

	private getTitleForCardLayout() {
		return this.isContentReadOnly() ? ContentConstants.SURVEY_ADMIN_OPEN_CLOSE_READONLY : ContentConstants.SURVEY_ADMIN_OPEN_CLOSE;
	}

	private getUtcDate(dateString?: string) {
		if (dateString) {
			return DateUtil.getUtcDateTime(new Date(dateString)).toString();
		}
		return undefined;
	}

	private onOpenOnChange(date?: any) {
		const { onChange, audienceId } = this.props;
		const { audience } = this.state;

		if (onChange) {
			onChange(audienceId, audience.openImmediately, this.getUtcDate(date), audience.closeOn);
		}
	}

	private onCloseOnChange(date?: any) {
		const { onChange, audienceId } = this.props;
		const { audience } = this.state;

		if (onChange) {
			onChange(audienceId, audience.openImmediately, audience.openOn, this.getUtcDate(date));
		}
	}

	private onOpenOnClear() {
		const { onChange, audienceId } = this.props;
		const { audience } = this.state;

		if (onChange) {
			onChange(audienceId, audience.openImmediately, undefined, audience.closeOn);
		}
	}

	private onCloseOnClear() {
		const { onChange, audienceId } = this.props;
		const { audience } = this.state;

		if (onChange) {
			onChange(audienceId, audience.openImmediately, audience.openOn, undefined);
		}
	}

	private isContentReadOnly() {
		const { audience } = this.state;
		if (audience.closeOn) {
			return moment(audience.closeOn).isBefore(moment());
		}
		return false;
	}

	private getDateContent() {
		if (this.isContentReadOnly()) {
			return (
				<>
					<LabelAbove type='input' textIsHtml={true} text={ContentConstants.SURVEY_ADMIN_OPEN_CLOSE_INSTRUCTIONS_READONLY} />
					<InputContainer>
						<Input
							aria-label='Open On'
							className={'tws-open-date-readonly'}
							readonly={true}
							value={DateUtil.formatDateToLocal(new Date(this.state.audience.openOn!))}
						/>
						<Input
							aria-label='Close On'
							className={'tws-close-date-readonly'}
							readonly={true}
							value={DateUtil.formatDateToLocal(new Date(this.state.audience.closeOn!))}
						/>
					</InputContainer>
				</>
			);
		}
		return this.getDateSelectionContent();
	}

	private getDateSelectionContent() {
		const { audience } = this.state;

		const validationErrorHelper = new ValidationErrorHelper<AudiencePropertiesUnderValidation>(this.getValidationError());
		const closeOnStartDate = moment(audience.openOn) < moment() ? moment() : moment(audience.openOn);
		const closeDateSelector = (
			<>
				<DatePicker
					aria-label='Close On'
					className={'tws-close-date-picker'}
					defaultGap={0}
					value={audience.closeOn}
					textInputPlaceholder={ContentConstants.DATEPICKER_CLOSE_DATE}
					clearText={ContentConstants.DATEPICKER_CLEARTEXT}
					enabledStartDate={closeOnStartDate}
					onChange={this.onCloseOnChange}
					onClear={this.onCloseOnClear}
					error={!validationErrorHelper.IsValid(AudiencePropertiesUnderValidation.CloseOn)}
					message={validationErrorHelper.Errors(AudiencePropertiesUnderValidation.CloseOn)}
					dateFormat={Constants.DATE_FORMAT_LONG}
				/>
			</>
		);
		let openDateSelector;
		if (moment(audience.openOn) < moment()) {
			openDateSelector = (
				<>
					<Input
						aria-label='Open On'
						className={'tws-open-date-readonly'}
						readonly={true}
						value={DateUtil.formatDateToLocal(new Date(audience.openOn!), Constants.DATE_FORMAT_LONG)}
					/>
				</>
			);
		} else {
			openDateSelector = (
				<>
					<OpenDatePicker
						aria-label='Open On'
						className={'tws-open-date-picker'}
						defaultGap={0}
						value={audience.openOn}
						textInputPlaceholder={ContentConstants.DATEPICKER_OPEN_DATE}
						clearText={ContentConstants.DATEPICKER_CLEARTEXT}
						enabledStartDate={moment()}
						onChange={this.onOpenOnChange}
						onClear={this.onOpenOnClear}
						error={!validationErrorHelper.IsValid(AudiencePropertiesUnderValidation.OpenOn)}
						message={validationErrorHelper.Errors(AudiencePropertiesUnderValidation.OpenOn)}
						dateFormat={Constants.DATE_FORMAT_LONG}
					/>
				</>
			);
		}
		return (
			<>
				<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_INSTRUCTIONS}>
					<LabelAbove type='input' textIsHtml={true} text={ContentConstants.SURVEY_ADMIN_OPEN_CLOSE_INSTRUCTIONS} />
					<InputContainer>
						{openDateSelector}
						{closeDateSelector}
					</InputContainer>

					{!validationErrorHelper.IsValid(AudiencePropertiesUnderValidation.OpenCloseDates) && (
						<ErrorMessageLabelNoIcon className='tws-open-close-dates-error-message'>
							{MessageHelper.getFormattedErrorMessages(validationErrorHelper.Errors(AudiencePropertiesUnderValidation.OpenCloseDates))}
						</ErrorMessageLabelNoIcon>
					)}
				</Col3Col9>
				<Col3Col9 label={ContentConstants.AUDIENCE_TIMEZONE_SECTION_LABEL}>
					<Timezone />
				</Col3Col9>
			</>
		);
	}

	private getSaveContent() {
		if (!this.isContentReadOnly()) {
			return (
				<>
					<Divider type='dashed' />
					<RightButtonContainer>
						<ButtonGroup type='column' reversed={false}>
							<Button id='SaveButton' color='primary' size='lg' onClick={this.handleSave}>
								{ContentConstants.BUTTON_SAVE_CHANGES}
							</Button>
						</ButtonGroup>
					</RightButtonContainer>
				</>
			);
		}
	}

	private handleSave() {
		const { audience } = this.state;

		this.props.update(audience, false);
	}

	private closeModal() {
		this.setState({ warningModalOpen: false });
	}
}
